import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FirstVisitService } from '@coin/shared/auth/feature';
import { StorageService } from '@coin/shared/core/data-access';
import { StorageKey } from '@coin/shared/core/util-enums';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import { asapScheduler } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SidebarComponent implements OnInit {
  private eRef = inject(ElementRef);
  private router = inject(Router);
  private firstVisit = inject(FirstVisitService);
  private storageService = inject(StorageService);

  readonly showSidebarOnStart = input(false);
  readonly titles = input<string[]>(undefined);
  readonly default = input(false);
  @Output() sidebarToggle = new EventEmitter<boolean>();
  @Output() selectItem = new EventEmitter<number>();

  @HostBinding('class.sidebar-visible')
  private get isVisibleClass() {
    return this.isSidebarVisible();
  }

  isSidebarVisible = signal(false);

  clickout = (event: MouseEvent): void => {
    if (!this.eRef.nativeElement.contains(event.target)) {
      if (this.isSidebarVisible()) {
        if (!this.firstVisit.status()) {
          this.onSidebarToggle();
        }
      }
    }
  };

  constructor() {
    const events = inject(GlobalEventsService);
    const destroyRef = inject(DestroyRef);

    events
      .listen('click')
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(event => this.clickout(event));
  }

  async ngOnInit(): Promise<void> {
    let lsItem: 'true' | 'false';
    if (this.default()) {
      lsItem = await this.storageService.getAsync(StorageKey.SIDEBAR_VISIBLE);
    }
    if (lsItem) {
      if (lsItem === 'false') {
        return;
      }
      if (lsItem === 'true') {
        asapScheduler.schedule(() => this.onSidebarToggle());
      }
    } else if (this.showSidebarOnStart() === true) {
      asapScheduler.schedule(() => this.onSidebarToggle());
    }
  }

  async onSidebarToggle(index?: number): Promise<void> {
    if (index !== undefined) {
      this.selectItem.emit(index);
    }
    if (this.titles()[index] === 'document-center') {
      this.router.navigate(['/document-center']);
    } else {
      this.isSidebarVisible.update(isVisible => !isVisible);
      if (this.default()) {
        await this.storageService.setAsync(
          StorageKey.SIDEBAR_VISIBLE,
          this.isSidebarVisible().toString() as 'true' | 'false'
        );
      }
      this.sidebarToggle.emit(this.isSidebarVisible());
    }
  }
}
